import { useState, useCallback } from "react"
import { UseInteractiveProps, UseInteractiveResult } from "./types"

export function useInteractive<P>(props: P = {} as P): UseInteractiveResult<P> {
  const {
    disabled: disabledProp,
    isDisabled,
    onFocus,
    onBlur,
    onClick,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    onMouseUp,
    onTouchStart,
    onTouchEnd,
    onPressOut,
    onPressIn,
    onHoverIn,
    onHoverOut,
    ...htmlProps
  } = props as UseInteractiveProps<P>

  const disabled = typeof isDisabled !== "undefined" ? isDisabled : disabledProp

  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const handleFocus = useCallback(
    (event: React.FocusEvent<Element>) => {
      if (disabled) {
        return
      }
      if (!isFocused) {
        setIsFocused(true)
      }
      onFocus?.(event)
    },
    [isFocused, disabled, onFocus],
  )

  const handleBlur = useCallback(
    (event: React.FocusEvent<Element>) => {
      if (disabled) {
        return
      }
      if (isFocused) {
        setIsFocused(false)
      }
      onBlur?.(event)
    },
    [isFocused, disabled, onBlur],
  )

  const handlePressUp = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      if (disabled) {
        return
      }
      if (!isPressed) {
        return
      }

      onPressOut?.(event)
      setIsPressed(false)
    },
    [isPressed, disabled, onPressOut],
  )

  const handleClick = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      if (disabled) {
        return
      }

      onClick?.(event as React.MouseEvent)
    },
    [disabled, onClick],
  )

  const handlePressDown = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      if (disabled) {
        return
      }
      if (isPressed) {
        return
      }
      onPressIn?.(event)
      if (!event.nativeEvent["_interactivePressHandled"]) {
        event.nativeEvent["_interactivePressHandled"] = true
        setIsPressed(true)
      }
    },
    [isPressed, disabled, onPressIn],
  )

  const handleHoverIn = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) {
        return
      }
      if (isHovered) {
        return
      }
      onHoverIn?.(event)
      setIsHovered(true)
    },
    [isHovered, disabled, onHoverIn],
  )

  const handleHoverOut = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) {
        return
      }
      if (!isHovered) {
        return
      }
      onHoverOut?.(event)
      setIsHovered(false)
    },
    [isHovered, disabled, onHoverOut],
  )

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) {
        return
      }
      handlePressDown(event)
      onMouseDown?.(event)
    },
    [disabled, handlePressDown, onMouseDown],
  )

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) {
        return
      }
      handleHoverIn(event)
      onMouseEnter?.(event)
    },
    [disabled, handleHoverIn, onMouseEnter],
  )

  const handleMouseLeave = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) {
        return
      }
      handlePressUp(event)
      handleHoverOut(event)
      onMouseLeave?.(event)
    },
    [disabled, handlePressUp, handleHoverOut, onMouseLeave],
  )

  const handleMouseUp = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) {
        return
      }
      handlePressUp(event)
      onMouseUp?.(event)
    },
    [disabled, handlePressUp, onMouseUp],
  )

  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      if (disabled) {
        return
      }
      handlePressDown(event)
      onTouchStart?.(event)
    },
    [disabled, handlePressDown, onTouchStart],
  )

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      if (disabled) {
        return
      }
      handlePressUp(event)
      onTouchEnd?.(event)
    },
    [disabled, handlePressUp, onTouchEnd],
  )

  const result = {
    ...htmlProps,
    isDisabled: disabled,
    isHovered,
    isPressed,
    isFocused,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onClick: handleClick,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
  } as UseInteractiveResult<P>

  return result
}
